import { useMemo } from 'react'
import styled, { type CSSProperties } from 'styled-components/macro'

import { Select } from '@loadsmart/loadsmart-ui'
import { type SelectProps } from '@loadsmart/loadsmart-ui/dist/components/Select/Select.types'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Field } from '@loadsmart/miranda-react'

import { MODES, type Mode } from '../../../domain/Mode'
import { mapToOptions, type Option } from '../../../domain/Option'
import { toArray } from '../../../utils/array'

const StyledField = styled(Field)`
  min-width: unset;
`

type BaseProps = Omit<SelectProps, 'value' | 'onChange' | 'multiple'> & {
  label: string
  style?: CSSProperties
  modes?: Array<Mode>
  value?: Array<Mode>
}

type SingleProps = BaseProps & {
  multiple: false
  value?: Mode
  onChange?: (value?: Mode) => void
}

type MultipleProps = BaseProps & {
  multiple: true
  value?: Array<Mode>
  onChange?: (value?: Array<Mode>) => void
}

export type ModeSelectProps = SingleProps | MultipleProps

export function ModeSelect({
  label,
  style,
  modes,
  value,
  id = 'mode',
  multiple = false,
  onChange,
  ...props
}: ModeSelectProps) {
  const selected = useMemo(() => {
    const options = mapToOptions({
      values: [...(value ?? [])],
      getValue: value => value,
      getLabel: value => value,
    })

    if (options.length === 0) {
      return undefined
    } else if (multiple) {
      return options
    } else {
      return options[0]
    }
  }, [multiple, value])

  const options = useMemo(
    () =>
      mapToOptions({
        values: [...(modes ?? MODES)],
        getValue: value => value,
        getLabel: label => label,
      }),
    [modes]
  )

  function handleOnChange(event: EventLike<Selectable | Selectable[] | null>) {
    const newModes = event?.target?.value as Array<Option> | Option | null

    const callback = onChange as (value?: Array<Mode> | Mode) => void
    const optionValues = toArray(newModes)

    if (optionValues.length === 0) {
      callback(undefined)
    } else if (multiple) {
      callback(optionValues.map(({ value }) => value as Mode))
    } else {
      callback(optionValues[0].value as Mode)
    }
  }

  return (
    <StyledField style={style}>
      {label && <Field.Label id={id}>{label}</Field.Label>}
      <Select
        id={id}
        options={options}
        value={selected}
        multiple={multiple}
        onChange={handleOnChange}
        {...props}
      />
    </StyledField>
  )
}
